import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {SelectItem} from 'primeng/api';
import {AutoComplete, AutoCompleteSelectEvent, AutoCompleteUnselectEvent} from 'primeng/autocomplete';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, take} from 'rxjs/operators';
import {ModalInterface} from '../../modal.interface';
import {ModalComponent} from '../../modal.component';
import {ModalState} from '../../modal-state.enum';
import {ModalStateEvent} from '../../modal-state-event';
import {MapComponent} from '../../../map/map.component';
import {MapService} from '../../../map/map.service';
import {PlaceCategoryApiModel} from '../../../../api/models/place-category.api-model';
import {PlaceRestService} from '../../../../api/services/place.rest.service';
import {UserLocationService} from '../../../../core/services/user-location.service';
import {TranslateService} from '@ngx-translate/core';
import {PlaceLabelService} from '../../../../features/place/place-label.service';

export interface PlaceFilterValues {
  name: string;
  parentCategories: string[];
  categories: string[];
  radius: number;
  origin?: {lat: number; lon: number};
  originLabel?: string;
}

@Component({
  selector: 'app-place-filters',
  templateUrl: './place-filters.component.html',
  styleUrls: ['./place-filters.component.scss'],
})
export class PlaceFiltersComponent implements ModalInterface, OnInit, OnDestroy {
  public data: {filters: PlaceFilterValues; categories: PlaceCategoryApiModel[]};
  public parent?: ModalComponent;
  public subject: Subject<ModalStateEvent>;
  public state: any;
  public form: UntypedFormGroup;
  public categories: PlaceCategoryApiModel[] = [];
  public searchResults: SelectItem[] = [];
  public mapActive = false;
  public initialCenter = {lat: 52.069, lon: 19.48};
  public initialMarker?: {lat: number; lon: number};
  private subscriptions: Subscription[] = [];
  private resultCountRequest = 0;

  @ViewChild(MapComponent) private mapComponent?: MapComponent;
  @ViewChild('placeAddress') private placeAddress?: AutoComplete;

  constructor(
    private readonly mapService: MapService,
    private readonly placesApi: PlaceRestService,
    private readonly userLocation: UserLocationService,
    private readonly translate: TranslateService,
    public readonly placeLabels: PlaceLabelService,
  ) {}

  ngOnInit(): void {
    const filters = this.data?.filters || {name: '', parentCategories: [], categories: [], radius: 100};
    const originLabel = this.isCoordinateLabel(filters.originLabel) ? undefined : filters.originLabel;
    const initialAddress = this.createAddressOption(filters.origin, originLabel);
    this.categories = this.data?.categories || [];
    this.form = new UntypedFormGroup({
      name: new UntypedFormControl(filters.name || ''),
      parentCategories: new UntypedFormControl(this.toArray(filters.parentCategories || (filters as any).parentCategory)),
      categories: new UntypedFormControl(this.toArray(filters.categories || (filters as any).category)),
      address: new UntypedFormControl(initialAddress ? [initialAddress] : []),
      radius: new UntypedFormControl(filters.radius || 100),
    });
    if (initialAddress) {
      this.searchResults = [initialAddress];
    }
    if (filters.origin) {
      this.initialCenter = filters.origin;
      this.initialMarker = filters.origin;
      if (!originLabel) {
        this.resolveAddressLabel(filters.origin);
      }
    }
    if (!this.categories.length) {
      this.subscriptions.push(this.placesApi.getCategories().subscribe(categories => this.categories = categories));
    }
    this.parent!.hasResetButton = true;
    this.subscriptions.push(this.state.subscribe((event: ModalStateEvent) => this.handleModalState(event)));
    this.subscriptions.push(this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => this.fetchResultsCount()));
    this.fetchResultsCount();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    if (this.parent) {
      this.parent.hasResetButton = false;
      this.parent.proceedButtonBadge = null;
      this.parent.resetButtonBadge = null;
    }
  }

  onModalShown(): void {
    this.mapActive = true;
  }

  onMapReady(): void {
    if (this.initialMarker) {
      this.updateMap(this.initialMarker);
    }
  }

  searchPlaces(event: {query: string}): void {
    const subscription = this.mapService.subscribeToQuerySubject().subscribe((results: any[]) => {
      this.searchResults = results.map(result => ({label: result.display_name, value: result}));
      subscription.unsubscribe();
    });
    this.mapService.searchPlaces(event.query);
  }

  selectPlace(event: AutoCompleteSelectEvent): void {
    const item = event.value?.value;
    if (!item || !Number.isFinite(Number(item.lat)) || !Number.isFinite(Number(item.lon))) {
      return;
    }
    const origin = {lat: Number(item.lat), lon: Number(item.lon)};
    this.setAddressSelection(event.value, origin);
  }

  unselectPlace(_event: AutoCompleteUnselectEvent): void {
    this.clearAddressSelection();
  }

  clearPlaceSelection(): void {
    this.clearAddressSelection();
  }

  selectMapPoint(point: {lat: number; lon: number}): void {
    this.initialCenter = point;
    this.initialMarker = point;
    this.updateMap(point);
    this.subscriptions.push(this.mapService.reverseGeocode(point.lat, point.lon).subscribe({
      next: result => {
        const value = result?.display_name ? result : {...result, lat: point.lat, lon: point.lon};
        this.setAddressSelection({label: value.display_name || this.currentLocationLabel, value}, point);
      },
      // The map point remains a valid origin if reverse geocoding is temporarily unavailable.
      error: () => this.setAddressSelection(this.createAddressOption(point), point),
    }));
  }

  updateRadius(): void {
    this.mapComponent?.updateCircleRadius(this.form.get('radius')?.value || 0);
  }

  toggleParentCategory(category: PlaceCategoryApiModel): void {
    const parentCategories = this.toggle(this.parentCategories, category.slug);
    const categories = parentCategories.includes(category.slug)
      ? this.categoriesSelection
      : this.categoriesSelection.filter(slug => this.categories.some(child => child.slug === slug && child.parentSlug !== category.slug));
    this.form.patchValue({parentCategories, categories});
  }

  toggleCategory(category: PlaceCategoryApiModel): void {
    this.form.patchValue({categories: this.toggle(this.categoriesSelection, category.slug)});
  }

  isParentCategorySelected(category: PlaceCategoryApiModel): boolean {
    return this.parentCategories.includes(category.slug);
  }

  isCategorySelected(category: PlaceCategoryApiModel): boolean {
    return this.categoriesSelection.includes(category.slug);
  }

  public categoryColor(category: PlaceCategoryApiModel): string {
    if (!category.parentSlug) {
      return category.color || 'var(--primary-color)';
    }

    const parent = this.categories.find(candidate => candidate.slug === category.parentSlug);
    if (parent?.color) {
      return parent.color;
    }
    if (category.color) {
      return category.color;
    }

    return 'var(--primary-color)';
  }

  get rootCategories(): PlaceCategoryApiModel[] {
    return this.categories.filter(category => !category.parentSlug);
  }

  get childCategories(): PlaceCategoryApiModel[] {
    return this.categories.filter(category => !!category.parentSlug && this.parentCategories.includes(category.parentSlug!));
  }

  get parentCategories(): string[] {
    return this.toArray(this.form?.get('parentCategories')?.value);
  }

  get categoriesSelection(): string[] {
    return this.toArray(this.form?.get('categories')?.value);
  }

  private handleModalState(event: ModalStateEvent): void {
    if (event.status === ModalState.HIDE) {
      this.clearResultCount();
      return;
    }
    if (event.status === ModalState.RESET) {
      this.clearResultCount();
      this.form.reset({name: '', parentCategories: [], categories: [], address: [], radius: 100}, {emitEvent: false});
      this.initialMarker = undefined;
      this.initialCenter = {lat: 52.069, lon: 19.48};
      this.updateRadius();
      this.restoreDefaultLocation();
      return;
    }
    if (event.status !== ModalState.PENDING) {
      return;
    }
    const selectedAddress = this.selectedAddress;
    const selectedOrigin = selectedAddress?.value;
    const origin = selectedOrigin && Number.isFinite(Number(selectedOrigin.lat)) && Number.isFinite(Number(selectedOrigin.lon))
      ? {lat: Number(selectedOrigin.lat), lon: Number(selectedOrigin.lon)}
      : this.initialMarker;
    this.subject.next(new ModalStateEvent(ModalState.SUCCESS, {
      name: String(this.form.get('name')?.value || '').trim(),
      parentCategories: this.parentCategories,
      categories: this.categoriesSelection,
      radius: Number(this.form.get('radius')?.value || 100),
      origin,
      originLabel: origin ? this.getAddressLabel(selectedAddress) : undefined,
    } as PlaceFilterValues));
  }

  private fetchResultsCount(): void {
    if (!this.parent) {
      return;
    }
    const request = ++this.resultCountRequest;
    this.parent.proceedButtonBadge = null;
    this.subscriptions.push(this.placesApi.getCollection(this.buildPreviewQuery()).pipe(take(1)).subscribe({
      next: result => {
        if (request === this.resultCountRequest && this.parent) {
          this.parent.proceedButtonBadge = String(result.total);
        }
      },
      error: () => {
        if (request === this.resultCountRequest && this.parent) {
          this.parent.proceedButtonBadge = null;
        }
      },
    }));
  }

  private clearResultCount(): void {
    ++this.resultCountRequest;
    if (!this.parent) {
      return;
    }
    this.parent.proceedButtonBadge = null;
    this.parent.resetButtonBadge = null;
  }

  private restoreDefaultLocation(): void {
    this.subscriptions.push(this.userLocation.resolveMapLocation().pipe(take(1)).subscribe({
      next: location => {
        if (location.coordinates) {
          const origin = {lat: location.coordinates.lat, lon: location.coordinates.lng};
          this.initialCenter = origin;
          this.initialMarker = origin;
          this.resolveAddressLabel(origin);
          return;
        }
        this.fetchResultsCount();
      },
      error: () => this.fetchResultsCount(),
    }));
  }

  private buildPreviewQuery(): {name?: string; categories?: string[]; parentCategories?: string[]; latitude?: number; longitude?: number; radius?: number; limit: number} {
    const selectedAddress = this.selectedAddress;
    const selectedOrigin = selectedAddress?.value;
    const origin = selectedOrigin && Number.isFinite(Number(selectedOrigin.lat)) && Number.isFinite(Number(selectedOrigin.lon))
      ? {lat: Number(selectedOrigin.lat), lon: Number(selectedOrigin.lon)}
      : this.initialMarker;

    return {
      name: String(this.form.get('name')?.value || '').trim() || undefined,
      categories: this.categoriesSelection.length ? this.categoriesSelection : undefined,
      parentCategories: this.parentCategories.length ? this.parentCategories : undefined,
      latitude: origin?.lat,
      longitude: origin?.lon,
      radius: origin ? Number(this.form.get('radius')?.value || 100) : undefined,
      limit: 1,
    };
  }

  public getAddressOptionValue(item: SelectItem): unknown {
    return item.value;
  }

  public getAddressOptionLabel(item: SelectItem): string | undefined {
    return item.label;
  }

  private get selectedAddress(): SelectItem | undefined {
    const addresses = this.form?.get('address')?.value;

    return Array.isArray(addresses) ? addresses[0] : undefined;
  }

  private get currentLocationLabel(): string {
    return this.translate.instant('places.filters.location_resolved');
  }

  private resolveAddressLabel(origin: {lat: number; lon: number}): void {
    this.subscriptions.push(this.mapService.reverseGeocode(origin.lat, origin.lon).pipe(take(1)).subscribe({
      next: result => this.setAddressSelection(
        this.createAddressOption(origin, result?.display_name || this.currentLocationLabel),
        origin,
      ),
      error: () => this.setAddressSelection(this.createAddressOption(origin), origin),
    }));
  }

  private setAddressSelection(address: SelectItem | null, origin: {lat: number; lon: number}): void {
    if (!address) {
      this.clearAddressSelection();
      return;
    }
    this.initialCenter = origin;
    this.initialMarker = origin;
    this.searchResults = [address];
    this.form.patchValue({address: [address]});
    this.updateMap(origin);
  }

  private clearAddressSelection(): void {
    this.initialMarker = undefined;
    this.mapComponent?.removeCircle();
    this.form.patchValue({address: []});
  }

  private createAddressOption(origin?: {lat: number; lon: number}, label?: string): SelectItem | null {
    if (!origin) {
      return null;
    }
    const resolvedLabel = label || this.currentLocationLabel;

    return {label: resolvedLabel, value: {display_name: resolvedLabel, lat: origin.lat, lon: origin.lon}};
  }

  private getAddressLabel(address: SelectItem | null | undefined): string | undefined {
    return typeof address?.label === 'string' && address.label.length ? address.label : undefined;
  }

  private isCoordinateLabel(label: string | undefined): boolean {
    return typeof label === 'string' && /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(label.trim());
  }

  private updateMap(origin: {lat: number; lon: number}): void {
    if (!this.mapComponent) {
      return;
    }
    this.mapComponent.updateMapCenter(origin);
    this.mapComponent.addCircleRadius(origin, this.form.get('radius')?.value || 0);
    this.mapComponent.waitUntilReady();
  }

  private toggle(values: string[], slug: string): string[] {
    return values.includes(slug) ? values.filter(value => value !== slug) : [...values, slug];
  }

  private toArray(value: unknown): string[] {
    const values = Array.isArray(value) ? value : [value];
    return values.filter((item): item is string => typeof item === 'string' && item.length > 0);
  }
}
